import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Grid,
    List,
    ListItem,
    ListItemButton,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import { Link as RouterLink, useHistory, useParams } from 'react-router-dom';
import debounce from 'lodash.debounce';
import api from '../../../../services/api';

const formatCurrencyBR = (value) => {
    const number = Number(value);
    if (Number.isNaN(number)) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(number);
};

const AccountReceivableForm = () => {
    const history = useHistory();
    const { id } = useParams();
    const isEdit = Boolean(id);

    const [appId, setAppId] = useState(null);
    const [status, setStatus] = useState({ type: '', message: '' });
    const [loading, setLoading] = useState(isEdit);
    const [salesOrderId, setSalesOrderId] = useState(null);
    const [summary, setSummary] = useState(null);

    const [orgSearch, setOrgSearch] = useState('');
    const [orgResults, setOrgResults] = useState([]);
    const [showOrgResults, setShowOrgResults] = useState(false);
    const [organization, setOrganization] = useState(null);

    const [description, setDescription] = useState('');
    const [amount, setAmount] = useState('');
    const [dueDate, setDueDate] = useState(new Date().toISOString().slice(0, 10));

    const [financialCategories, setFinancialCategories] = useState([]);
    const [costCenters, setCostCenters] = useState([]);
    const [paymentTerms, setPaymentTerms] = useState([]);
    const [financialCategory, setFinancialCategory] = useState('');
    const [costCenter, setCostCenter] = useState('');
    const [paymentTerm, setPaymentTerm] = useState('');

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get('v1/apps/default/');
                setAppId(res.data.app.id);
            } catch (error) {
                console.error(error);
                setStatus({ type: 'error', message: 'Erro ao buscar app padrão' });
            }
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        (async () => {
            try {
                const [fc, cc, pt] = await Promise.all([
                    api.get(`v1/apps/${appId}/financial-categories/?type=revenue`),
                    api.get(`v1/apps/${appId}/cost-centers/`),
                    api.get(`v1/apps/${appId}/payment-terms/`)
                ]);
                setFinancialCategories(fc.data || []);
                setCostCenters(cc.data || []);
                setPaymentTerms(pt.data || []);
            } catch (error) {
                console.error(error);
            }
        })();
    }, [appId]);

    useEffect(() => {
        if (!appId || !isEdit) return;
        (async () => {
            try {
                const res = await api.get(`v1/apps/${appId}/receivable/${id}/`);
                const data = res.data;
                setDescription(data.description);
                setSummary(data);
                setOrganization({ id: data.organization, name: data.organization_name });
                setOrgSearch(data.organization_name);
                setSalesOrderId(data.sales_order);
                setFinancialCategory(data.financial_category || '');
                setCostCenter(data.cost_center || '');
                setPaymentTerm(data.payment_term || '');
            } catch (error) {
                console.error(error);
                setStatus({ type: 'error', message: 'Erro ao carregar conta' });
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, isEdit, id]);

    const handleOrgSearch = useMemo(
        () =>
            debounce(async (value, currentAppId) => {
                if (!value || !currentAppId) {
                    setOrgResults([]);
                    setShowOrgResults(false);
                    return;
                }
                try {
                    const res = await api.get(`v1/apps/${currentAppId}/organizations/?search=${encodeURIComponent(value)}`);
                    setOrgResults(res.data.organizations || []);
                    setShowOrgResults(true);
                } catch (error) {
                    console.error(error);
                }
            }, 400),
        []
    );

    const handleOrgSearchChange = (e) => {
        const value = e.target.value;
        setOrgSearch(value);
        setOrganization(null);
        handleOrgSearch(value, appId);
    };

    const selectOrganization = async (org) => {
        setOrganization(org);
        setOrgSearch(org.name);
        setShowOrgResults(false);

        if (!appId) return;
        try {
            const res = await api.get(`v1/apps/${appId}/organizations/${org.id}/`);
            const data = res.data?.organization;
            if (!data) return;
            setFinancialCategory((prev) => prev || data.default_financial_category || '');
            setCostCenter((prev) => prev || data.default_cost_center || '');
            setPaymentTerm((prev) => prev || data.default_payment_term || '');
        } catch (error) {
            console.error(error);
        }
    };

    const selectedPaymentTerm = paymentTerms.find((t) => t.id === paymentTerm);
    const installmentsPreview =
        !isEdit && selectedPaymentTerm && selectedPaymentTerm.installments > 1 && amount
            ? `Isso vai gerar ${selectedPaymentTerm.installments} parcelas de ~${formatCurrencyBR(
                  Number(amount) / selectedPaymentTerm.installments
              )} a cada ${selectedPaymentTerm.interval_days} dias`
            : null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus({ type: '', message: '' });

        if (!appId) {
            setStatus({ type: 'error', message: 'App não carregado ainda' });
            return;
        }
        if (!organization) {
            setStatus({ type: 'error', message: 'Selecione um cliente' });
            return;
        }
        if (!description || (!isEdit && (!amount || !dueDate))) {
            setStatus({ type: 'error', message: 'Preencha descrição, valor e vencimento' });
            return;
        }

        const payload = {
            organization: organization.id,
            description,
            financial_category: financialCategory || null,
            cost_center: costCenter || null,
            payment_term: paymentTerm || null
        };
        if (!isEdit) {
            payload.amount = amount;
            payload.due_date = dueDate;
        }

        try {
            if (isEdit) {
                await api.patch(`v1/apps/${appId}/receivable/${id}/`, payload);
            } else {
                await api.post(`v1/apps/${appId}/receivable/`, payload);
            }
            history.push('/dashboard/financial/receivable');
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao salvar conta a receber' });
        }
    };

    if (loading) return null;

    return (
        <Box>
            <Typography variant="h3" gutterBottom sx={{ mb: 2 }}>
                {isEdit ? 'Editar Conta a Receber' : 'Nova Conta a Receber'}
            </Typography>

            <Card>
                <CardContent>
                    {salesOrderId && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Gerada automaticamente pelo{' '}
                            <RouterLink to={`/dashboard/sales/${salesOrderId}/`}>Pedido de Venda</RouterLink>.
                        </Alert>
                    )}

                    {status.message && (
                        <Alert severity={status.type} sx={{ mb: 2 }}>
                            {status.message}
                        </Alert>
                    )}

                    {isEdit && summary && (
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                            <Chip label={`Total: ${formatCurrencyBR(summary.total_amount)}`} />
                            <Chip label={`Recebido: ${formatCurrencyBR(summary.total_paid)}`} color="success" variant="outlined" />
                            <Chip label={`Saldo: ${formatCurrencyBR(summary.remaining_balance)}`} color="warning" variant="outlined" />
                            <Typography variant="body2" color="text.secondary">
                                As parcelas e recebimentos são gerenciados na lista de Contas a Receber.
                            </Typography>
                        </Stack>
                    )}

                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} sx={{ position: 'relative' }}>
                                <TextField
                                    label="Cliente"
                                    fullWidth
                                    required
                                    value={orgSearch}
                                    onChange={handleOrgSearchChange}
                                    onFocus={() => orgResults.length > 0 && setShowOrgResults(true)}
                                    placeholder="Buscar por nome ou CNPJ..."
                                />
                                {showOrgResults && orgResults.length > 0 && (
                                    <Paper sx={{ position: 'absolute', zIndex: 10, width: '100%', maxHeight: 240, overflowY: 'auto' }}>
                                        <List dense>
                                            {orgResults.map((org) => (
                                                <ListItem key={org.id} disablePadding>
                                                    <ListItemButton onClick={() => selectOrganization(org)}>
                                                        {org.name} {org.cnpj ? `- ${org.cnpj}` : ''}
                                                    </ListItemButton>
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Paper>
                                )}
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Descrição"
                                    fullWidth
                                    required
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                />
                            </Grid>

                            {!isEdit && (
                                <>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Vencimento da 1ª parcela"
                                            type="date"
                                            fullWidth
                                            required
                                            InputLabelProps={{ shrink: true }}
                                            value={dueDate}
                                            onChange={(e) => setDueDate(e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Valor total"
                                            type="number"
                                            fullWidth
                                            required
                                            inputProps={{ min: 0, step: '0.01' }}
                                            value={amount}
                                            onChange={(e) => setAmount(e.target.value)}
                                            helperText={installmentsPreview}
                                        />
                                    </Grid>
                                </>
                            )}

                            <Grid item xs={12} sm={4}>
                                <TextField
                                    select
                                    label="Categoria Financeira"
                                    fullWidth
                                    value={financialCategory}
                                    onChange={(e) => setFinancialCategory(e.target.value)}
                                >
                                    <MenuItem value="">Nenhuma</MenuItem>
                                    {financialCategories.map((c) => (
                                        <MenuItem key={c.id} value={c.id}>
                                            {c.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField select label="Centro de Custo" fullWidth value={costCenter} onChange={(e) => setCostCenter(e.target.value)}>
                                    <MenuItem value="">Nenhum</MenuItem>
                                    {costCenters.map((c) => (
                                        <MenuItem key={c.id} value={c.id}>
                                            {c.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    select
                                    label="Condição de Pagamento"
                                    fullWidth
                                    disabled={isEdit}
                                    value={paymentTerm}
                                    onChange={(e) => setPaymentTerm(e.target.value)}
                                    helperText={isEdit ? 'Não editável após a conta já ter sido gerada' : undefined}
                                >
                                    <MenuItem value="">Nenhuma</MenuItem>
                                    {paymentTerms.map((t) => (
                                        <MenuItem key={t.id} value={t.id}>
                                            {t.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12}>
                                <Box display="flex" justifyContent="flex-end" gap={2}>
                                    <Button onClick={() => history.push('/dashboard/financial/receivable')}>Voltar</Button>
                                    <Button type="submit" variant="contained">
                                        {isEdit ? 'Salvar Alterações' : 'Criar Conta'}
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </form>
                </CardContent>
            </Card>
        </Box>
    );
};

export default AccountReceivableForm;
